import useChartGrouping from '@/composables/chart-grouping';
import useDebounce from 'shared/composables/debounce';
import useGoals from '@/composables/goal/goals';
import useParamEnricher from '@/composables/view-params/enrich';
import useProperties from '@/composables/property/property';
import { AND, CREATED_AT, EQ, HAS, MODIFIED_AT, NOT, RESULT, TYPE, UID } from 'shared/api/query/constants';
import { DateTime } from 'luxon';
import { GoalActivityFilterHandler } from '@/lib/filter/goal-activity/handler';
import { GoalFilterHandler } from '@/lib/filter/goal/handler';
import { TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { UNASSIGNED_VALUE } from '@/composables/grid-page/chart/utils';
import { UpdatesFilterHandler } from '@/lib/filter/updates/handler';
import { aggregatorType, chartGroupByOption, gridPageChartType, timeSeriesInterval } from 'shared/constants.json';
import { buildCustomFuncProgressCourseListPred } from '@/api/query/custom-func-helper';
import { combine, notHasFilter } from 'shared/api/query/filter';
import { computed, ref, shallowRef, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { createGoalCycleFilter } from '@/api/query/nebula/goal-cascade';
import { dogma } from '@/api';
import { goalActivity as goalActivityConfig, goal as goalConfig, update as updateConfig } from 'shared/api/query/configs.json';
import { goalsInCycleVarBlock } from '@/api/query/nebula/goal';
import { logCatch } from '@/lib/logger/logger';

// Goals inside a planning are unpublished (publishedAt == null). Charts always
// hide them — same convention as `usePublishedAtFilter` / GoalsExplorer.
const excludePlanningGoalsFilter = { func: { name: HAS, attr: goalConfig.edges.publishedAt } };

const generateTimeSeries = (startDate, endDate, interval = timeSeriesInterval.weekly, now = DateTime.local()) => {
  const intervalConfig = { weeks: 0, months: 0 };

  switch (interval) {
    case timeSeriesInterval.monthly:
      intervalConfig.months = 1;
      break;
    case timeSeriesInterval.daily:
      intervalConfig.days = 1;
      break;
    case timeSeriesInterval.weekly:
    default:
      intervalConfig.weeks = 1;
      break;
  }

  const timeSeries = [];
  let t = startDate;

  if (now.diff(t) < 0) {
    return [t];
  }

  while (t.diff(endDate) < 0 && t.diff(now) < 0) {
    timeSeries.push(t);
    t = t.plus(intervalConfig);
  }

  if (now.diff(endDate) < 0) {
    timeSeries.push(now);
  }

  if (now.diff(endDate) > 0) {
    timeSeries.push(endDate);
  }

  return timeSeries;
};

export default function useChartDataQuery(chart) {
  const loading = ref(false);
  // Shallow: the raw dogma response is read-only plain data. Deep reactivity
  // would walk the (potentially large) tree on every access.
  const resultRaw = shallowRef(null);
  const error = ref(null);
  // Anchor for time-series ranges. Kept stable across re-queries so minor
  // timestamp drift doesn't bust downstream reactive chains; refreshed only
  // after STABLE_NOW_TTL_MS so long-lived dashboards don't drift past midnight.
  const stableNow = ref(DateTime.local());
  const STABLE_NOW_TTL_MS = 10 * 60 * 1000;
  const refreshStableNow = () => {
    const now = DateTime.local();
    if (now.toMillis() - stableNow.value.toMillis() >= STABLE_NOW_TTL_MS) {
      stableNow.value = now;
    }
  };

  const { debounce } = useDebounce({ onBeforeUnmountCancel: true });

  const { processChartData } = useChartGrouping(chart, resultRaw);

  const goalFilterHandler = new GoalFilterHandler();
  const updatesFilterHandler = new UpdatesFilterHandler();
  const goalActivityFilterHandler = new GoalActivityFilterHandler();

  const propertySvc = useProperties();
  const goalsSvc = useGoals();
  const paramEnricher = useParamEnricher(propertySvc, goalsSvc);

  const enrichedFilter = computed(() => {
    if (chart.value.filter === null) {
      return null;
    }
    return paramEnricher.enrich(copy(chart.value.filter));
  });

  const filter = computed(() => {
    switch (chart.value.source) {
      case goalConfig.model: {
        const { filter: goalCycleFilter, varBlocks: goalCycleVarBlocks } = createGoalCycleFilter(chart.value.goalCycles);
        if (enrichedFilter.value === null) {
          return {
            filterTree: combine(AND, [excludePlanningGoalsFilter, goalCycleFilter]),
            varBlocks: goalCycleVarBlocks,
          };
        }
        const filterObject = goalFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: combine(AND, [excludePlanningGoalsFilter, goalCycleFilter, filterObject.filterTree]),
          varBlocks: [...filterObject.varBlocks, ...goalCycleVarBlocks],
        };
      }
      case updateConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = updatesFilterHandler.Translate(enrichedFilter.value);
        const checkInFilter = { op: NOT, child: [{ func: { name: HAS, attr: updateConfig.edges.goal } }] };
        return {
          filterTree: combine(AND, [checkInFilter, filterObject.filterTree]),
          varBlocks: filterObject.varBlocks,
        };
      }
      case goalActivityConfig.model: {
        if (enrichedFilter.value === null) {
          return { filterTree: null, varBlocks: [] };
        }
        const filterObject = goalActivityFilterHandler.Translate(enrichedFilter.value);
        return {
          filterTree: filterObject.filterTree,
          varBlocks: filterObject.varBlocks,
        };
      }
      default:
        return { filterTree: null, varBlocks: [] };
    }
  });

  const isGoalCycleGroupingWithFilter = computed(() => chart.value.source === goalConfig.model
    && !chart.value.groupBy.isProperty
    && chart.value.groupBy.edge === goalConfig.edges.goalCycle
    && chart.value.goalCycles.length > 0);

  const stackableChartTypes = [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column, gridPageChartType.table];

  const hasStacking = computed(() => stackableChartTypes.includes(chart.value.chartType)
    && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const getEdge = (prop) => (prop.isProperty ? 'cachedProperty' : prop.edge);

  const getLangs = (prop) => (prop.isProperty ? [`p${prop.property.uid}`] : null);

  const isNumericAggregator = (agg) => [aggregatorType.avg, aggregatorType.sum, aggregatorType.min, aggregatorType.max].includes(agg);

  // Edges that are always set on every record — there are no rows missing them,
  // so the "unassigned" / "no value" query blocks can be skipped.
  const alwaysSetEdges = [CREATED_AT, MODIFIED_AT, goalConfig.edges.cachedCalculatedCurrent];
  const isAlwaysSet = (prop) => !prop.isProperty && alwaysSetEdges.includes(prop.edge);

  // Extracts the aggregation predicate into a uid-scoped variable, then
  // aggregates `val(var)` outside @groupby. Required for sum/avg/min/max
  // because cachedProperty is a `string @lang` predicate Dgraph cannot
  // aggregate directly — but val(var) is parsed as a number.
  const buildVarValBlocks = ({ resultAlias, varName, filterTree }) => {
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    return [
      {
        alias: 'var',
        func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
        filter: filterTree,
        model: chart.value.source,
        children: [{
          attr: getEdge(chart.value.aggregationConfig.aggregation),
          langs: getLangs(chart.value.aggregationConfig.aggregation),
          var: varName,
        }],
      },
      {
        alias: resultAlias,
        children: [{
          alias: aggregator,
          attr: 'val',
          isInternal: true,
          needsVar: [{ name: varName, typ: 2 }],
          func: { name: aggregator, needsVar: [{ name: varName, typ: 2 }] },
        }],
      },
    ];
  };

  const buildNotHasFilter = (groupBy) => {
    if (groupBy.isProperty) {
      return { func: { name: EQ, attr: 'cachedProperty', lang: `p${groupBy.property.uid}`, args: [{ value: '' }] } };
    }
    return notHasFilter(groupBy.edge);
  };

  const buildUnassignedFilter = () => {
    const notHasFilter = buildNotHasFilter(chart.value.groupBy);

    // When grouping by goalCycle edge, the base filter includes the goalCycleFilter
    // which contradicts "NOT has(goalCycle)". Use only the user filter in that case,
    // but still exclude planning goals.
    if (chart.value.source === goalConfig.model
      && !chart.value.groupBy.isProperty
      && chart.value.groupBy.edge === goalConfig.edges.goalCycle) {
      const userFilterTree = enrichedFilter.value !== null
        ? goalFilterHandler.Translate(enrichedFilter.value).filterTree
        : null;
      return combine(AND, [excludePlanningGoalsFilter, userFilterTree, notHasFilter]);
    }

    return filter.value.filterTree
      ? combine(AND, [filter.value.filterTree, notHasFilter])
      : notHasFilter;
  };

  // Dgraph's @groupby parser rejects `val()` as an aggregator argument
  // (parser.go:3128), and its executor refuses sum/avg/min/max on string
  // predicates — so we cannot aggregate cachedProperty server-side. For
  // property-numeric aggregations we fetch one row per goal and fold
  // per (group, stack) on the client.
  const isPropertyNumericAgg = computed(() => {
    const agg = chart.value.aggregationConfig.aggregation;
    return agg.isProperty === true && isNumericAggregator(agg.aggregator);
  });

  const buildPropertyNumericRowChildren = () => {
    const groupBy = chart.value.groupBy;
    const stack = chart.value.aggregationConfig.stackOptions;
    const agg = chart.value.aggregationConfig.aggregation;
    const children = [
      { alias: 'val', attr: getEdge(groupBy), langs: getLangs(groupBy) },
      { alias: 'aggValue', attr: 'cachedProperty', langs: getLangs(agg) },
    ];
    if (hasStacking.value) {
      children.push({ alias: 'stackGroup', attr: getEdge(stack), langs: getLangs(stack) });
    }
    return children;
  };

  // `has` alone keeps "" values; `not eq("")` alone keeps unset langs
  // because Dgraph treats absence as not-equal — so we need both.
  const aggHasValueFilter = () => {
    const lang = `p${chart.value.aggregationConfig.aggregation.property.uid}`;
    return combine(AND, [
      { func: { name: HAS, attr: 'cachedProperty', lang } },
      { op: NOT, child: [{ func: { name: EQ, attr: 'cachedProperty', lang, args: [{ value: '' }] } }] },
    ]);
  };

  const buildPropertyNumericBlock = (alias, scopeFilter) => ({
    alias,
    func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
    filter: combine(AND, [scopeFilter, aggHasValueFilter()]),
    model: chart.value.source,
    children: buildPropertyNumericRowChildren(),
  });

  const buildChildrenForAggregator = (aggregator) => {
    switch (aggregator) {
      case aggregatorType.count:
      case aggregatorType.countDistinct:
        return [{ attr: UID, isCount: true }];
      case aggregatorType.avg:
      case aggregatorType.sum:
      case aggregatorType.min:
      case aggregatorType.max:
        return [
          { attr: UID, isCount: true },
          { alias: aggregator, attr: getEdge(chart.value.aggregationConfig.aggregation), func: { name: aggregator } },
        ];
      default:
        return null;
    }
  };

  const buildUnassignedQueryBlocks = () => {
    if (chart.value.groupBy.groupBy === chartGroupByOption.none) {
      return [];
    }

    // When grouping by goalCycle with a global cycle filter, goals without a cycle
    // cannot exist in the result set, so skip the unassigned query entirely.
    if (isGoalCycleGroupingWithFilter.value) {
      return [];
    }

    // Skip when grouping by an always-set edge — no records will be missing it.
    if (isAlwaysSet(chart.value.groupBy)) {
      return [];
    }

    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const isNumeric = isNumericAggregator(aggregator);
    const children = buildChildrenForAggregator(aggregator);
    if (!children) return [];

    const unassignedFilter = buildUnassignedFilter();
    const { stackOptions } = chart.value.aggregationConfig;

    // Property-numeric: fetch raw rows for stacked unassigned. For non-stacked
    // we fall through to the var/val pattern below, which works.
    if (isPropertyNumericAgg.value && hasStacking.value) {
      return [buildPropertyNumericBlock('unassigned', unassignedFilter)];
    }

    const base = {
      alias: 'unassigned',
      func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
      filter: unassignedFilter,
      model: chart.value.source,
    };

    if (hasStacking.value) {
      base.isGroupby = true;
      base.groupbyAttrs = [{ alias: 'stackGroup', attr: getEdge(stackOptions), langs: getLangs(stackOptions) }];
      if (aggregator === aggregatorType.countDistinct) {
        base.groupbyAttrs.push({ alias: 'subGroup', attr: getEdge(chart.value.aggregationConfig.aggregation), langs: getLangs(chart.value.aggregationConfig.aggregation) });
      }
    }

    // Non-stacked numeric aggregators need var/val pattern (Dgraph requires it outside @groupby)
    if (!hasStacking.value && isNumeric) {
      return buildVarValBlocks({ resultAlias: 'unassigned', varName: 'unassignedVal', filterTree: unassignedFilter });
    }

    const blocks = [{ ...base, children }];

    // Items missing both the main groupBy attr AND the stack attr
    if (hasStacking.value && !isAlwaysSet(stackOptions)) {
      const notHasStackFilter = buildNotHasFilter(stackOptions);
      const noStackUnassignedFilter = combine(AND, [unassignedFilter, notHasStackFilter]);

      if (isNumeric) {
        // Use var/val pattern for numeric aggregators (Dgraph requires it outside @groupby)
        blocks.push(...buildVarValBlocks({ resultAlias: 'unassignedNoStack', varName: 'unassignedNoStackVal', filterTree: noStackUnassignedFilter }));
      } else {
        // For count/countDistinct, simple count without @groupby
        blocks.push({
          alias: 'unassignedNoStack',
          func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
          filter: noStackUnassignedFilter,
          model: chart.value.source,
          children: [{ attr: UID, isCount: true }],
        });
      }
    }

    return blocks;
  };

  // Adapt one goal's row into the shape `groupResult` / `addToGroupedRes`
  // (chart-grouping.js) expect for one item: a synthetic single-element
  // bucket they can fold into per-(group, stack) totals.
  const transformRawRows = (rows) => {
    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const stacked = hasStacking.value;
    return rows.reduce((acc, row) => {
      const parsed = parseFloat(row.aggValue);
      if (Number.isNaN(parsed)) return acc;
      const item = { val: row.val, count: 1, [aggregator]: parsed };
      if (stacked) item.stackGroup = row.stackGroup;
      acc.push(item);
      return acc;
    }, []);
  };

  const decodeUnassignedValue = (data) => {
    const { aggregator } = chart.value.aggregationConfig.aggregation;

    // Always-set edges have no missing records — skip the "unassigned" category entirely.
    if (isAlwaysSet(chart.value.groupBy)) {
      return null;
    }

    if (hasStacking.value) {
      const entries = isPropertyNumericAgg.value
        ? transformRawRows(data.unassigned ?? [])
        : [...(data.unassigned?.[0]?.['@groupby'] ?? [])];
      // Items missing both the main groupBy and stack attr go into the "No value" stack group
      const noStackRaw = data.unassignedNoStack?.[0];
      if (noStackRaw) {
        const hasValue = aggregator === aggregatorType.count || aggregator === aggregatorType.countDistinct
          ? (noStackRaw.count ?? 0) > 0
          : noStackRaw[aggregator] != null;
        if (hasValue) {
          entries.push({ ...noStackRaw, stackGroup: UNASSIGNED_VALUE });
        }
      }
      return entries;
    }

    // Non-stacked: return a single number
    const raw = data.unassigned?.[0];
    if (!raw) return 0;

    switch (aggregator) {
      case aggregatorType.count:
        return raw.count ?? 0;
      case aggregatorType.countDistinct:
        return 0;
      case aggregatorType.avg:
      case aggregatorType.sum:
      case aggregatorType.min:
      case aggregatorType.max:
        return raw[aggregator] ?? 0;
      default:
        return 0;
    }
  };

  // Items that have the main groupBy attr but NOT the stack attr (per-category "No value" stack)
  const buildNoStackQueryBlocks = () => {
    if (!hasStacking.value) return [];
    const { stackOptions } = chart.value.aggregationConfig;

    // Skip when stacking by an always-set edge — no records will be missing it.
    if (isAlwaysSet(stackOptions)) return [];

    const { aggregator } = chart.value.aggregationConfig.aggregation;
    const children = buildChildrenForAggregator(aggregator);
    if (!children) return [];

    const notHasStackFilter = buildNotHasFilter(stackOptions);
    const noStackFilter = filter.value.filterTree
      ? combine(AND, [filter.value.filterTree, notHasStackFilter])
      : notHasStackFilter;

    if (isPropertyNumericAgg.value) {
      return [buildPropertyNumericBlock('resNoStack', noStackFilter)];
    }

    const valGroupbyAttrs = [{
      alias: 'val',
      attr: getEdge(chart.value.groupBy),
      langs: getLangs(chart.value.groupBy),
    }];

    const block = {
      alias: 'resNoStack',
      func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
      filter: noStackFilter,
      model: chart.value.source,
      isGroupby: true,
      groupbyAttrs: valGroupbyAttrs,
      children,
    };

    if (aggregator === aggregatorType.countDistinct) {
      block.groupbyAttrs = [
        ...valGroupbyAttrs,
        { alias: 'subGroup', attr: getEdge(chart.value.aggregationConfig.aggregation), langs: getLangs(chart.value.aggregationConfig.aggregation) },
      ];
    }

    return [block];
  };

  const appendStackOptions = (groupbyAttrs) => {
    if (!hasStacking.value) {
      return groupbyAttrs;
    }
    const { stackOptions } = chart.value.aggregationConfig;
    return [
      ...groupbyAttrs,
      { alias: 'stackGroup', attr: getEdge(stackOptions), langs: getLangs(stackOptions) },
    ];
  };

  const isTimeSeriesMode = computed(() => TIME_SERIES_AGGREGATORS.includes(chart.value.aggregationConfig?.aggregation?.aggregator));

  const timeSeriesData = computed(() => {
    if (!isTimeSeriesMode.value || chart.value.goalCycles.length === 0) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    // Get cycle date range from selected cycles
    const cycles = chart.value.goalCycles;
    const startDate = cycles.reduce((min, cycle) => {
      const cycleStart = DateTime.fromISO(cycle.start);
      return min === null || cycleStart < min ? cycleStart : min;
    }, null);

    const endDate = cycles.reduce((max, cycle) => {
      const cycleEnd = DateTime.fromISO(cycle.end);
      return max === null || cycleEnd > max ? cycleEnd : max;
    }, null);

    if (!startDate || !endDate) {
      return { timeSeries: [], startDate: null, endDate: null };
    }

    const interval = chart.value.aggregationConfig.timeSeriesInterval || 'weekly';
    const timeSeries = generateTimeSeries(startDate, endDate, interval, stableNow.value);

    return { timeSeries, startDate, endDate };
  });

  const query = computed(() => {
    if (isTimeSeriesMode.value) {
      const { timeSeries } = timeSeriesData.value;
      if (timeSeries.length === 0) {
        return [];
      }

      const cycleGoalsVar = 'cycle_goals';
      const goalCycles = chart.value.goalCycles;
      const goalFilter = enrichedFilter.value !== null
        ? goalFilterHandler.Translate(enrichedFilter.value)
        : { filterTree: null, varBlocks: [] };
      const filterTree = combine(AND, [excludePlanningGoalsFilter, goalFilter.filterTree]);

      return [
        ...goalsInCycleVarBlock({ goalCycles, filter: filterTree, varName: cycleGoalsVar }),
        ...goalFilter.varBlocks,
        {
          alias: RESULT,
          model: goalConfig.model,
          needsVar: [{ name: cycleGoalsVar, typ: 1 }],
          func: { name: UID, needsVar: [{ name: cycleGoalsVar, typ: 1 }] },
          filter: filterTree,
          default: [],
          children: [
            buildCustomFuncProgressCourseListPred({ timeSeries, attr: UID }),
          ],
        },
      ];
    }
    switch (chart.value.chartType) {
      case gridPageChartType.metric:
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count: {
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                children: [
                  { attr: UID, isCount: true },
                ],
              },
            ];
          }
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: [{
                  attr: getEdge(chart.value.aggregationConfig.aggregation),
                  langs: getLangs(chart.value.aggregationConfig.aggregation),
                }],
              },
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            return [
              ...filter.value.varBlocks,
              ...buildVarValBlocks({ resultAlias: 'res', varName: 'edgeVal', filterTree: filter.value.filterTree }),
            ];
          }
          default:
            return [];
        }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        const unassignedBlocks = buildUnassignedQueryBlocks();
        const noStackBlocks = buildNoStackQueryBlocks();
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          case aggregatorType.countDistinct:
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([
                  {
                    alias: 'val',
                    attr: getEdge(chart.value.groupBy),
                    langs: getLangs(chart.value.groupBy),
                  },
                  {
                    alias: 'subGroup',
                    attr: getEdge(chart.value.aggregationConfig.aggregation),
                    langs: getLangs(chart.value.aggregationConfig.aggregation),
                  },
                ]),
                children: [
                  { attr: UID, isCount: true },
                ],
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max: {
            if (isPropertyNumericAgg.value) {
              return [
                ...filter.value.varBlocks,
                buildPropertyNumericBlock('res', filter.value.filterTree),
                ...noStackBlocks,
                ...unassignedBlocks,
              ];
            }
            return [
              ...filter.value.varBlocks,
              {
                alias: 'res',
                func: { name: TYPE, args: [{ value: chart.value.source, typ: 1 }] },
                filter: filter.value.filterTree,
                model: chart.value.source,
                isGroupby: true,
                groupbyAttrs: appendStackOptions([{
                  alias: 'val',
                  attr: getEdge(chart.value.groupBy),
                  langs: getLangs(chart.value.groupBy),
                }]),
                children: buildChildrenForAggregator(chart.value.aggregationConfig.aggregation.aggregator),
              },
              ...noStackBlocks,
              ...unassignedBlocks,
            ];
          }
          default:
            return [];
        }
      }
      default:
        return [];
    }
  });

  const decodeTimeSeries = (progressCourseList) => {
    const aggregator = chart.value.aggregationConfig?.aggregation?.aggregator;
    const base = {
      isTimeSeries: true,
      progressData: [],
      statusScoreData: [],
      statusDistributionData: [],
      startDate: timeSeriesData.value.startDate,
      endDate: timeSeriesData.value.endDate,
    };

    if (!progressCourseList || progressCourseList.length === 0) {
      return base;
    }

    if (aggregator === aggregatorType.statusScoreOverTime) {
      base.statusScoreData = progressCourseList.map((point) => {
        const date = DateTime.fromISO(point.date);
        const list = point.list ?? [];
        if (list.length === 0) {
          return { x: date.toMillis(), y: 0 };
        }
        const avg = list.reduce((acc, g) => acc + (g.statusOption?.score ?? 0), 0) / list.length;
        return { x: date.toMillis(), y: Math.round(avg * 10) / 10 };
      });
      return base;
    }

    if (aggregator === aggregatorType.statusDistributionOverTime) {
      base.statusDistributionData = progressCourseList.map((point) => {
        const date = DateTime.fromISO(point.date);
        const statusCounts = {};
        (point.list ?? []).forEach((g) => {
          const opt = g.statusOption;
          if (!opt?.uid) {
            return;
          }
          statusCounts[opt.uid] = (statusCounts[opt.uid] || 0) + 1;
        });
        return { x: date.toMillis(), statusCounts };
      });
      return base;
    }

    base.progressData = progressCourseList.map((point) => {
      const date = DateTime.fromISO(point.date);
      const progressValues = point.list?.map((g) => g.progress) ?? [];
      const avgProgress = progressValues.length > 0
        ? progressValues.reduce((sum, p) => sum + p, 0) / progressValues.length
        : 0;
      return { x: date.toMillis(), y: Math.round(avgProgress) };
    });
    return base;
  };

  const decodeResponse = (data) => {
    if (isTimeSeriesMode.value) {
      return decodeTimeSeries(data.result?.[0]?.progressCourseList);
    }

    switch (chart.value.chartType) {
      case gridPageChartType.metric:
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
            return data.res?.[0]?.count ?? 0;
          case aggregatorType.avg:
            return data.res?.[0]?.avg ?? 0;
          case aggregatorType.sum:
            return data.res?.[0]?.sum ?? 0;
          case aggregatorType.min:
            return data.res?.[0]?.min ?? 0;
          case aggregatorType.max:
            return data.res?.[0]?.max ?? 0;
          case aggregatorType.countDistinct:
            return data.res?.[0]?.['@groupby']?.length ?? 0;
          default:
            return 0;
        }
      case gridPageChartType.pie:
      case gridPageChartType.line:
      case gridPageChartType.bar:
      case gridPageChartType.table:
      case gridPageChartType.column: {
        if (Object.keys(data).length === 0) {
          return { series: [], total: 0 };
        }
        const groupByData = isPropertyNumericAgg.value
          ? transformRawRows(data.res ?? [])
          : (data.res?.[0]?.['@groupby'] ?? []);
        const hasUnassignedQuery = chart.value.groupBy.groupBy !== chartGroupByOption.none
          && !isGoalCycleGroupingWithFilter.value;
        const unassignedValue = hasUnassignedQuery
          ? decodeUnassignedValue(data)
          : null;
        const noStackData = isPropertyNumericAgg.value
          ? transformRawRows(data.resNoStack ?? [])
          : (data.resNoStack?.[0]?.['@groupby'] ?? []);
        if (groupByData.length === 0 && unassignedValue === null && noStackData.length === 0) {
          return { series: [], total: 0 };
        }
        switch (chart.value.aggregationConfig.aggregation.aggregator) {
          case aggregatorType.count:
          case aggregatorType.avg:
          case aggregatorType.sum:
          case aggregatorType.min:
          case aggregatorType.max:
          case aggregatorType.countDistinct:
            return processChartData(groupByData, unassignedValue, noStackData);
          default:
            return { series: [], total: 0 };
        }
      }
      default:
        return { series: [], total: 0 };
    }
  };

  const result = computed(() => {
    if (resultRaw.value === null || loading.value) {
      return null;
    }
    return decodeResponse(resultRaw.value);
  });

  const runQuery = (debounceDelay = 0) => {
    refreshStableNow();
    loading.value = true;
    const run = () => {
      if (query.value.length === 0) {
        loading.value = false;
        return;
      }
      dogma.query(query.value)
        .then((res) => {
          // Frozen: response is never mutated, and freezing keeps V8's hidden
          // classes stable across the many reads from processChartData.
          resultRaw.value = res.data ? Object.freeze(res.data) : res.data;
          error.value = null;
        })
        .catch(logCatch((e) => {
          error.value = e;
        }))
        .finally(() => {
          loading.value = false;
        });
    };
    debounce(run, debounceDelay);
  };

  watch(query, (newVal, oldVal) => {
    if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
      return;
    }
    runQuery(50);
  }, { deep: true });

  runQuery();

  return {
    runQuery,
    result,
    error,
    loading,
  };
}
